<!-- 
Expander panel showing the non-compliance history: late payment calculation.
-->
<template>
  <div>
    <a
      :id="'focus-' + panelNumber"
      href="#" class="section-heading subheading d-block pl-4 pr-5 pt-3 pb-3"
      @click.stop.prevent="$emit('click:togglePanel', panelLatePaymentScore)"
    >
      <v-icon class="non-compliance-history-breakdown">subdirectory_arrow_right</v-icon>
      Non-compliance history and registration renewal: Late payment value
      <v-btn icon class="focus-button" tabindex="-1">
        <v-icon :class="'expand-icon ' + (expanded ? 'expanded' : '')">expand_more</v-icon>
      </v-btn>
    </a>
    <v-expand-transition>
      <v-card v-show="expanded" flat>
        <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
        <div v-if="!isInternal" class="caption pl-4 pr-4 pt-2" v-html="templates.LatePaymentScore"/>
        <v-card-text class="pt-2 px-0 pb-0">
          <v-layout row wrap mx-4>
            <v-flex 
              xs12 sm4 mt-2 pt-1 
              class="text-xs-right"
            >
              <our-data-field v-if="sanctioned" label="Late payment measure" label-width="11">
                Sanctioned
                <br>
                (maximum risk factor value applies)
              </our-data-field>
              <our-data-field v-if="!sanctioned" label="Late payment measure formula" label-width="11">
                <our-multiline-text :text="latePaymentMeasureFormula"/>
              </our-data-field>
              <our-data-field v-if="!sanctioned" label="Late payment measure" label-width="11">
                <our-multiline-text :text="latePaymentMeasureCalculation" no-margin/>
                <span v-if="latePaymentMeasureCalculation">= </span>
                <precise-risk-factor-number 
                  :value="levyCalculation.latePaymentMeasure"
                  :decimal-places="1"
                  :levy-collection-id="levyCollection.id" 
                  :estimator-key="estimatorKey" 
                  type="NonComplianceHistory"
                />
              </our-data-field>
            </v-flex>
            <v-flex xs12 sm4 pb-4>
              <risk-factor-table
                :estimator-key="estimatorKey" 
                :levy-collection-id="levyCollection.id" 
                :selected-score="levyCalculation.latePaymentScore"
                type="NonComplianceHistory" 
                title="Late payment measure"
              />
            </v-flex>
            <v-flex 
              xs12 sm4 mt-2 pt-1 
              class="text-xs-right"
            >
              <our-data-field label="Late payment value" typography="title" label-width="11">
                {{ levyCalculation.latePaymentScore | numericInput2 }}
                <template v-slot:footer>
                  <playground-up-down v-if="isLevyEstimator" field="latePaymentScore" show-original small/>
                </template>
              </our-data-field>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-expand-transition>
  </div>
</template>

<script>
import * as formatting from "@/helpers/formatting.helper";
import filterMixins from "@/mixins/filter.mixins";
import mixins from "./mixins";
import PlaygroundUpDown from "@/components/levy-collection-providers/calculation/PlaygroundUpDown.vue";
import RiskFactorTable from "@/components/levy-collection-providers/calculation/RiskFactorTable.vue";
import PreciseRiskFactorNumber from "@/components/levy-collection-providers/calculation/PreciseRiskFactorNumber.vue";

export default {
  components: {
    PlaygroundUpDown,
    RiskFactorTable,
    PreciseRiskFactorNumber
  },
  mixins: [filterMixins, mixins],
  props: {
    expanded: {
      type: Boolean,
      required: true
    },
    panelNumber: {
      type: Number,
      required: true
    }
  },
  computed: {
    sanctioned() {
      return this.levyParameters.sanctioned && this.levyCollection.enableSanctions;
    },
    latePaymentMeasureFormula() {
      const acYears = this.levyCollection.numberOfAnnualChargeLatePaymentYears;
      const ltpsYears = this.levyCollection.numberOfLtpsLatePaymentYears;
      const description = [];
      const type = this.isVsl ? "VSL" : this.isHelp ? "HELP" : this.isUpfront ? "Up-front" : "UNKNOWN";
      for (let i = 1; i <= 5; i++) {
        const useAnnualCharge = acYears >= i;
        const useLtps = ltpsYears >= i;
        const year = this.levyCollectionYear - i;
        const field =
          i == 1 ? "latePaymentWeightingForPreviousYear" : `latePaymentWeightingForPreviousYearMinus${i - 1}`;
        const weighting = formatting.numericInput3(this.levyCollection[field]);
        if (useAnnualCharge && useLtps) {
          description.push(`(${year} ${type} annual charge + ${type} levy days late) * ${weighting}`);
        } else if (useAnnualCharge) {
          description.push(`${year} ${type} annual charge days late * ${weighting}`);
        } else if (useLtps) {
          description.push(`${year} ${type} levy days late * ${weighting}`);
        }
      }
      return description.length > 0 ? description.join("\n + ") : "Not applicable";
    },
    latePaymentMeasureCalculation() {
      const acYears = this.levyCollection.numberOfAnnualChargeLatePaymentYears;
      const ltpsYears = this.levyCollection.numberOfLtpsLatePaymentYears;
      const description = [];
      const zero = v => (v == null ? 0 : v);
      for (let i = 1; i <= 5; i++) {
        const useAnnualCharge = acYears >= i;
        const useLtps = ltpsYears >= i;
        const field =
          i == 1 ? "latePaymentWeightingForPreviousYear" : `latePaymentWeightingForPreviousYearMinus${i - 1}`;
        const weighting = formatting.numericInput3(this.levyCollection[field]);
        const suffix = i == 1 ? "InPreviousYear" : `InPreviousYearMinus${i - 1}`;
        const acDaysLate = zero(this.levyParameters[`annualChargeLatePaymentDays${suffix}`]);
        const ltpsDaysLate = zero(this.levyParameters[`ltpsLevyLatePaymentDays${suffix}`]);
        if (useAnnualCharge && useLtps) {
          description.push(`(${acDaysLate} + ${ltpsDaysLate}) * ${weighting}`);
        } else if (useAnnualCharge) {
          description.push(`${acDaysLate} * ${weighting}`);
        } else if (useLtps) {
          description.push(`${ltpsDaysLate} * ${weighting}`);
        }
      }
      if (description.length > 0) {
        return `${description.join("\n + ")}`;
      } else {
        return "";
      }
    }
  }
};
</script>
